import React, {useState, useEffect, forwardRef, useImperativeHandle} from "react";
import {Table,Header,Box,Button,Alert}from "@amzn/awsui-components-react/polaris";
import {fetchCredentialAuditData} from "../utils/credentialManagementAPIs";
import {formatDateWithTime} from "../../../common/utils/apiUtils";

const CredentialTable = forwardRef((params, ref) => {
    const {showData = false, useCase, subUseCase} = params;

    const [credentialData, setCredentialData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [error, setError] = useState(null);

    // Fetch data when showData is true and both useCase and subUseCase are available
    useEffect(() => {
        if (showData && useCase && subUseCase) {
            setLoading(true);
            setError(null); // Clear previous errors
            fetchCredentialAuditData(setCredentialData, useCase, subUseCase, setError)
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [showData, useCase, subUseCase]);

    const onSelectionChange = (item) => {
        setSelectedItems(item.detail.selectedItems)
    }

    const handleRefresh = () => {
        if (useCase && subUseCase) {
            setLoading(true);
            setError(null);
            fetchCredentialAuditData(setCredentialData, useCase, subUseCase,setError)
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    useImperativeHandle(ref, () => ({
        handleRefresh
    }));

    if (!showData) {
        return null;
    }

    if (error) {
        return (
            <div>
                <Header variant="h2">Credential Details</Header>
                <Alert
                    type="error"
                    header={error.type === 'unauthorized' ? 'Access Denied' : 'Error'}
                    action={
                        <Button onClick={handleRefresh} iconName="refresh">
                            Retry
                        </Button>
                    }
                >
                    {error.message}
                </Alert>
            </div>
        );
    }
    if (!loading && credentialData.length === 0) {
        return null;
    }
        return (
        <Table
            header={<Header variant="h2">Credential Details</Header>}
            loading={loading}
            selectionType="single"
            onSelectionChange={onSelectionChange}
            selectedItems={selectedItems}
            loadingText="Loading credentials..."
            columnDefinitions={[
                {
                    id: "useCase",
                    header: "Country",
                    cell: e => e.useCase,
                    sortingField: "useCase"
                },
                {
                    id: "subUseCase",
                    header: "Compliance",
                    cell: e => e.subUseCase,
                    sortingField: "subUseCase"
                },
                {
                    id: "credentialType",
                    header: "Credential Type",
                    cell: e => e.credentialType,
                    sortingField: "credentialType"
                },
                {
                    id: "filingIdentifier",
                    header: "Filing Identifier",
                    cell: e => e.filingIdentifier,
                    sortingField: "filingIdentifier"
                },
                {
                    id: "updatedBy",
                    header: "Uploaded By",
                    cell: e => e.updatedBy,
                    sortingField: "updatedBy"
                },
                {
                    id: "updatedAt",
                    header: "Upload Time",
                    cell: e => formatDateWithTime(e.updatedAt),
                    sortingField: "updatedAt"
                },
                {
                    id: "expiryAt",
                    header: "Expiry Date",
                    cell: e => e.expiryAt ? formatDateWithTime(e.expiryAt) : "",
                    sortingField: "expiryAt"
                }
            ]}
            visibleColumns={[
                "useCase",
                "subUseCase",
                "credentialType",
                "filingIdentifier",
                "updatedBy",
                "updatedAt",
                "expiryAt"
            ]}
            empty={
                <Box textAlign="center" color="inherit">
                    <b>No credentials found</b>
                    <Box
                        padding={{ bottom: "s" }}
                        variant="p"
                        color="inherit">
                        No credential data to display for the selected country and compliance.
                    </Box>
                </Box>
            }
            items={credentialData}
            preferences={
                <Button iconName="refresh" variant="icon" onClick={handleRefresh} />
            }
        />
    );
});

export default CredentialTable;
